export const PRODUCT_STATUS_META = {
  nao_iniciado: {
    label: 'Não iniciado',
    labelKey: 'SUPPORT_TICKETS.STATUS.NOT_STARTED',
    className: 'bg-slate-100 text-slate-700',
  },
  em_andamento: {
    label: 'Em andamento',
    labelKey: 'SUPPORT_TICKETS.STATUS.IN_PROGRESS',
    className: 'bg-sky-50 text-sky-700',
  },
  finalizado: {
    label: 'Finalizado',
    labelKey: 'SUPPORT_TICKETS.STATUS.FINISHED',
    className: 'bg-emerald-50 text-emerald-700',
  },
};

export const WAITING_ON_META = {
  support: {
    label: 'Aguardando suporte',
    labelKey: 'SUPPORT_TICKETS.WAITING_ON.SUPPORT',
    className: 'bg-amber-50 text-amber-700',
  },
  customer: {
    label: 'Aguardando sua resposta',
    labelKey: 'SUPPORT_TICKETS.WAITING_ON.CUSTOMER',
    className: 'bg-rose-50 text-rose-700',
  },
  none: {
    label: 'Sem pendência',
    labelKey: 'SUPPORT_TICKETS.WAITING_ON.NONE',
    className: 'bg-emerald-50 text-emerald-700',
  },
};

export const SLA_STATUS_META = {
  on_track: {
    label: 'No prazo',
    labelKey: 'SUPPORT_TICKETS.SLA.STATUS.ON_TRACK',
    className: 'bg-emerald-50 text-emerald-700',
  },
  breached: {
    label: 'SLA vencido',
    labelKey: 'SUPPORT_TICKETS.SLA.STATUS.BREACHED',
    className: 'bg-rose-50 text-rose-700',
  },
  paused: {
    label: 'SLA pausado',
    labelKey: 'SUPPORT_TICKETS.SLA.STATUS.PAUSED',
    className: 'bg-amber-50 text-amber-700',
  },
  met: {
    label: 'SLA cumprido',
    labelKey: 'SUPPORT_TICKETS.SLA.STATUS.MET',
    className: 'bg-slate-100 text-slate-700',
  },
};

export const ATTACHMENT_LIMIT_BYTES = 40 * 1024 * 1024;
export const SUPPORTED_ATTACHMENT_TYPES = [
  'image/',
  'application/pdf',
  'text/plain',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
export const SUPPORTED_ATTACHMENT_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.txt',
];

const translatedLabel = (meta, t) => {
  if (meta?.labelKey && typeof t === 'function') {
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    return t(meta.labelKey);
  }

  return meta?.label;
};

export const normalizePortalMeta = meta => ({
  total: Number(meta?.total || 0),
  waitingOnCustomer: Number(meta?.waiting_on_customer || 0),
  mine: Number(meta?.mine || 0),
  canViewAccountTickets: Boolean(meta?.can_view_account_tickets),
  publicHelpEnabled: meta?.public_help_enabled !== false,
  publicHelpBasePath: meta?.public_help_base_path || '',
});

export const ticketStatusMeta = (status, { t } = {}) => {
  const meta = PRODUCT_STATUS_META[status] || {
    label: status || 'Não iniciado',
    labelKey: status ? '' : 'SUPPORT_TICKETS.STATUS.NOT_STARTED',
    className: 'bg-slate-100 text-slate-700',
  };

  return { ...meta, label: translatedLabel(meta, t) || meta.label };
};

export const waitingOnMeta = (waitingOn, { t } = {}) => {
  const meta = WAITING_ON_META[waitingOn] || {
    label: waitingOn || 'Sem pendência',
    labelKey: waitingOn ? '' : 'SUPPORT_TICKETS.WAITING_ON.NONE',
    className: 'bg-slate-100 text-slate-700',
  };

  return { ...meta, label: translatedLabel(meta, t) || meta.label };
};

export const slaStatusMeta = (status, { t } = {}) => {
  const meta = SLA_STATUS_META[status] || SLA_STATUS_META.on_track;
  return { ...meta, label: translatedLabel(meta, t) || meta.label };
};

export const ticketNeedsResponse = ticket =>
  ticket?.waiting_on_customer || ticket?.waiting_on === 'customer';

export const formatTicketDate = (value, locale = 'pt-BR') => {
  if (!value) return 'Sem data';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sem data';

  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

export const formatAttachmentSize = bytes => {
  const size = Number(bytes || 0);
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  if (size >= 1024) return `${Math.ceil(size / 1024)} KB`;
  return `${size} B`;
};

export const isSupportedAttachment = file => {
  const name = String(file?.name || '').toLowerCase();
  const type = String(file?.type || '').toLowerCase();
  return (
    SUPPORTED_ATTACHMENT_TYPES.some(allowed => type.startsWith(allowed)) ||
    SUPPORTED_ATTACHMENT_EXTENSIONS.some(extension => name.endsWith(extension))
  );
};

export const isImageAttachment = attachment => {
  const filename = String(
    attachment?.filename || attachment?.name || ''
  ).toLowerCase();
  const contentType = String(
    attachment?.content_type || attachment?.type || ''
  ).toLowerCase();
  return (
    contentType.startsWith('image/') ||
    /\.(png|jpe?g|gif|webp|bmp)$/.test(filename)
  );
};

export const publicSupportHref = ({ basePath = '', locale = 'pt-BR' } = {}) => {
  if (!basePath) return '';
  const normalizedBase = String(basePath).replace(/\/+$/g, '');
  const normalizedLocale = String(locale || 'pt-BR').replace('_', '-');
  return `${normalizedBase}/${normalizedLocale}`;
};
